import { observer } from 'mobx-react-lite';
import PropTypes from 'prop-types';
import { colors } from '../../../../../../colorVars';
import { useWebsocketStore } from '../../../../../../stores/websocketStore/websocketProvider';
import BidAskBox from '../../../../charts/d3/candlestickchart/newCandleChart/chartHeader/BidAskBox';
import NotInPositionButtonArray from '../notInPositionButtonArray/NotInPositionButtonArray';
import PnLDisplay from '../pnLDisplay/PnLDisplay';
import {
  definingSetupDetails,
  definingTradeDirection,
  determineStatus,
} from '../tradePlanIdentityDisplay/identityDisplayUtils';
import {
  mapAccountsFromBalances,
  resolveMarketDataBrokerage,
} from '../../../../../../utils/accountRouting';
import { MarketDataHealthDot } from '../marketDataHealthDot/MarketDataHealthDot';
import style from './NotInPositionTopRow.module.css';
import { TopRowBox } from './topRowBox/TopRowBox';
import { TopRowFillPrice } from './topRowBox/TopRowFillPrice';
import { TopRowExpandButton } from './topRowExpandButton/TopRowExpandButton';
const directionColorMap = {
  SHORT: colors.red,
  LONG: colors.green,
  BOTH: colors.blue,
};
const statusColorMap = {
  ARMED: colors.blue,
  ENTERED: colors.green,
  PAUSED: colors.orange,
  DEACTIVATED: colors.lightGrey,
};
const SELECTED_TOP_ROW_GLOW = `0 0 0 1.5px ${colors.blue}, 0 0 9px 2px ${colors.blue}99`;

export const NotInPositionTopRow = observer(({ tradePlanId }) => {
  const { tradePlanStore, accountBalanceStore, tradePlanSelectionStore } = useWebsocketStore();
  const tradePlan = tradePlanStore.tradePlans[tradePlanId] ?? null;
  if (!tradePlan) return null;
  const isPending = tradePlan.__pending === true;
  const selected = tradePlanSelectionStore.isSelected(tradePlanId);
  const accounts = mapAccountsFromBalances(accountBalanceStore?.balances);
  const marketDataBrokerage = resolveMarketDataBrokerage({
    accountNumber: tradePlan.accountNumber,
    accounts,
    currentBrokerage: tradePlan.brokerage,
    dataSource: tradePlan.dataSource,
    symbol: tradePlan.symbol,
  });
  const direction = definingTradeDirection(tradePlan);
  const status = isPending ? 'LOADING' : determineStatus(tradePlan);
  const isOrbScanning =
    !isPending &&
    tradePlan.setup === 'ORB' &&
    ((tradePlanStore.softOrderIdsByTradePlan[tradePlanId]?.length ?? 0) === 0);
  const statusLabel = isOrbScanning ? 'WAITING' : status;
  const statusColor = isPending
    ? colors.orange
    : isOrbScanning
      ? colors.lightGrey
      : statusColorMap[status];

  return (
    <div className={style.container}>
      <div
        className={style.topRow}
        style={
          selected
            ? { boxShadow: SELECTED_TOP_ROW_GLOW, borderRadius: '4px' }
            : undefined
        }
      >
        <TopRowBox
          backgroundColor={colors.grey}
          color={colors.lightGrey}
          width="100px"
        >
          <>
            {tradePlan.symbol}
            <MarketDataHealthDot status={tradePlan.marketDataHealthStatusNormalized} />
          </>
        </TopRowBox>
        <TopRowBox
          backgroundColor={colors.grey}
          color={directionColorMap[direction]}
          width="100px"
        >
          {direction}
        </TopRowBox>
        <TopRowBox
          backgroundColor={colors.grey}
          color={statusColor}
          width="100px"
        >
          {statusLabel}
        </TopRowBox>
        {tradePlan.savedConfigurationTitle ? (
          <TopRowBox
            backgroundColor={colors.grey}
            color={colors.lightGrey}
            width="205px"
            justifyContent={
              tradePlan.savedConfigurationTitle.length > 20
                ? 'flexStart'
                : 'center'
            }
          >
            {tradePlan.savedConfigurationTitle}
          </TopRowBox>
        ) : (
          <TopRowBox
            backgroundColor={colors.grey}
            color={colors.lightGrey}
            width="205px"
          >
            {isPending ? 'Creating Trade Plan...' : definingSetupDetails(tradePlan)}
          </TopRowBox>
        )}
      </div>
      <div className={style.thirdRow}>
        <TopRowFillPrice tradePlanId={tradePlanId} />

        <PnLDisplay tradePlanId={tradePlanId} />
        <BidAskBox
          symbol={tradePlan.symbol}
          dataSource={tradePlan.dataSource}
          brokerage={marketDataBrokerage}
        />
      </div>
      <div className={style.thirdRow}>
        <NotInPositionButtonArray tradePlanId={tradePlanId} />
      </div>
      <div className={style.bottomRow}>
        <TopRowExpandButton
          height="24px"
          width="520px"
          backgroundColor={colors.grey}
          fill={isPending ? colors.grey : colors.lightGrey}
          clickHandler={() => {
            if (!isPending) {
              tradePlanStore.flipExpandedState(tradePlanId);
            }
          }}
          expanded={tradePlan.expanded}
        />
      </div>
    </div>
  );
});

NotInPositionTopRow.propTypes = {
  expanded: PropTypes.bool,
  setExpanded: PropTypes.func,
  tradePlanId: PropTypes.string,
};
